import moment from 'moment';
import { useCallback, useContext } from 'react';
import { useRecoilValue } from 'recoil';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useFilters from '@ubeya/shared/hooks/account/useFilters';
import { selectedBranch } from '@ubeya/shared/atoms/shared';
import usePublications from '@ubeya/shared/hooks/account/usePublications';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import * as api from '@ubeya/shared/services/api';
import { getTimeRangeWithDates } from '@ubeya/shared/utils/time';
import { uuidv4 } from '@ubeya/shared/utils/uuid';
import { isUndefined, omitBy } from 'lodash';
import ComplianceViolationContext from '@ubeya/shared/contexts/ComplianceViolationContext';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import { BOOKING_ACTIONS } from 'scheduling/constants';
import { SchedulingContextDispatcher, SchedulingContextSelector } from 'scheduling/contexts/SchedulingContext';

/**
 * Calculate the week boundaries for a given date based on account's firstDayOfWeek
 * @param {string|moment} date - The date to calculate week for
 * @param {number} firstDayOfWeek - Account's first day of week (1=Sunday, 2=Monday, etc.)
 * @returns {{ weekStartDate: string, weekEndDate: string }} Week start and end in YYYY-MM-DD format
 */
const getWeekBoundaries = (date, firstDayOfWeek) => {
  const momentDate = moment(date);
  // Convert account's firstDayOfWeek (1-7 where 1=Sunday) to moment's format (0-6 where 0=Sunday)
  const momentFirstDay = (firstDayOfWeek || 1) - 1;

  // Get the day of week for the given date (0-6)
  const currentDayOfWeek = momentDate.day();

  // Calculate days to subtract to get to the first day of the week
  let daysToSubtract = currentDayOfWeek - momentFirstDay;
  if (daysToSubtract < 0) {
    daysToSubtract += 7;
  }

  const weekStart = momentDate.clone().subtract(daysToSubtract, 'days');
  const weekEnd = weekStart.clone().add(6, 'days');

  return {
    weekStartDate: weekStart.format('YYYY-MM-DD'),
    weekEndDate: weekEnd.format('YYYY-MM-DD')
  };
};

const useTimeAvailabilityBookingOptions = ({ refetch }) => {
  const { accountId, config } = useAccount();
  const { refetchWarnings } = useContext(SchedulingContextSelector);
  const { firstDayOfWeek } = config;
  const selectedBranchId = useRecoilValue(selectedBranch);

  const { filters } = useFilters();
  const { mappedLabels } = useLabels();
  const { locations } = useLocations();

  const {
    addShiftWithEmployee,
    removeSchedule,
    removeShift,
    updateSlot,
    editProject,
    addSchedules,
    moveSlot,
    addProjects,
    addSchedule
  } = useContext(SchedulingContextDispatcher);

  // Compliance violation error handling from context
  const { wrapApiCall } = useContext(ComplianceViolationContext) || {};

  const { updatePublications } = usePublications({
    publicationIds: []
  });

  const getOrCreateBulkProject = useCallback(
    async ({ projects }) => {
      const formattedProjects = projects.map(
        ({
          labelId: baseLabelId,
          clientBranchId,
          clientAdminId,
          locationId: baseLocationId,
          branchCostCenterId,
          needed = 1,
          date,
          startTime,
          endTime,
          positionId,
          isOpenShift = true,
          shiftGroupId = undefined,
          areaId,
          shiftTags
        }) => {
          const labelId = baseLabelId ? parseInt(baseLabelId, 10) : undefined;

          const locationId = baseLocationId || (labelId ? mappedLabels[labelId]?.locationId : locations[0]?.id);

          // Always use full week boundaries for weekly planner projects
          const { weekStartDate, weekEndDate } = getWeekBoundaries(date, firstDayOfWeek);

          const formattedProject = {
            labelId,
            locationId,
            date: weekStartDate,
            endDate: weekEndDate,
            clientBranchId,
            clientAdminId,
            isWeeklyPlanner: true,
            shifts: [
              {
                labelId,
                branchCostCenterId,
                needed,
                positionId,
                startTime,
                endTime,
                isOpenShift,
                shiftGroupId,
                shiftTags,
                ...(areaId && { locationId: areaId })
              }
            ]
          };

          return formattedProject;
        }
      );
      const { data } = (await addProjects({ projects: formattedProjects, isSchedulingBoard: true })) || {};

      return data;
    },
    [addProjects, firstDayOfWeek, locations, mappedLabels]
  );

  //onBookToOpenShift => onBookToShift -- no openShift indication
  const onBookToExistingShift = useCallback(
    async ({ projectId, shiftId, employeeId, bookToLinkedShifts = false, bypassComplianceWarnings = false }) => {
      // If booking to linked shifts, use bulk booking endpoint
      if (bookToLinkedShifts) {
        const actionPayload = {
          projectId,
          shifts: [{ shiftId, employeeIds: [employeeId], bookToLinkedShifts }],
          bypassComplianceWarnings
        };

        if (wrapApiCall) {
          await wrapApiCall(() => addSchedules(actionPayload), {
            actionFn: () => {
              // Use closure variables - no parameters needed
              return addSchedules({
                ...actionPayload,
                bypassComplianceWarnings: true
              });
            },
            onSuccess: () => refetchWarnings?.()
          });
        } else {
          await addSchedules(actionPayload);
          refetchWarnings?.();
        }

        if (employeeId != null) {
          trackEvent('Employees Booked', {
            module: 'Scheduling',
            context: 'Time availability',
            projectId,
            employeeIds: [employeeId],
            origin: 'Scheduling Board'
          });
        }

        return { shift: {} };
      }

      const addSchedulePayload = {
        branchId: selectedBranchId,
        projectId,
        shiftId,
        user: { id: employeeId },
        bypassComplianceWarnings
      };

      let shift = {};
      if (wrapApiCall) {
        const result = await wrapApiCall(() => addSchedule(addSchedulePayload), {
          actionFn: () => {
            return addSchedule({ ...addSchedulePayload, bypassComplianceWarnings: true });
          },
          onSuccess: () => refetchWarnings?.()
        });
        shift = result?.shift || {};
      } else {
        const result = await addSchedule(addSchedulePayload);
        shift = result?.shift || {};
        refetchWarnings?.();
      }

      trackEvent('Employees Booked', {
        module: 'Scheduling',
        context: 'Time availability',
        projectId,
        employeeIds: [employeeId],
        origin: 'Scheduling Board'
      });

      return { shift };
    },
    [addSchedule, addSchedules, refetchWarnings, selectedBranchId, wrapApiCall]
  );

  const getOrCreateProjectAndBook = useCallback(
    async ({
      labelId,
      locationId,
      clientBranchId,
      clientAdminId,
      date,
      needed = 1,
      employeeId,
      startTime,
      endTime,
      positionId,
      branchCostCenterId,
      refetchProjects,
      isOpenShift = true,
      shiftGroupId,
      bypassComplianceWarnings
    }) => {
      const response = await getOrCreateBulkProject({
        projects: [
          {
            labelId,
            needed,
            locationId,
            clientBranchId,
            clientAdminId,
            date,
            startTime,
            endTime,
            branchCostCenterId,
            positionId,
            isOpenShift,
            shiftGroupId
          }
        ]
      });

      // get the most updated shift id - the max
      // add here if needed comparison with other variavles to make
      // sure it's the relevant shift - positionId, start & end, etc
      const shiftId = Math.max(...(response?.[0]?.shifts || []).map(({ id }) => id));

      const projectId = response?.[0]?.id;

      const { shift = {} } = await onBookToExistingShift({
        projectId,
        shiftId,
        employeeId,
        refetchProjects: false,
        bypassComplianceWarnings
      });

      const addedSlot = shift?.addedSlots?.[0];
      refetchProjects && refetch();
      refetchWarnings?.();

      return {
        timesheet: addedSlot?.timesheet,
        shiftData: {
          shiftId: addedSlot?.shiftId,
          employeeSlot: addedSlot?.employeeSlotId,
          slotId: addedSlot?.id,
          projectId
        }
      };
    },
    [getOrCreateBulkProject, onBookToExistingShift, refetch, refetchWarnings]
  );

  const bookBulkEmployeesToShift = useCallback(
    async ({ projectId, shifts }) => {
      const actionPayload = {
        projectId,
        shifts,
        bypassComplianceWarnings: false
      };

      if (wrapApiCall) {
        try {
          await wrapApiCall(() => addSchedules(actionPayload), {
            actionFn: () => {
              return addSchedules({
                ...actionPayload,
                bypassComplianceWarnings: true
              });
            },
            onSuccess: () => refetchWarnings?.()
          });
        } catch (error) {
          // If error was handled by wrapApiCall (compliance violation), don't re-throw
          // The modal is already showing, so we just need to stop execution
          if (error._handled) {
            // Re-throw to stop Promise.all execution, but mark it so outer handlers know it's handled
            return;
          }
          // Re-throw other errors normally
          throw error;
        }
      } else {
        await addSchedules(actionPayload);
        refetchWarnings?.();
      }

      const employeeIds = (shifts || []).flatMap((s) => s.employeeIds || []).filter((id) => id != null);
      if (employeeIds.length > 0) {
        trackEvent('Employees Booked', {
          module: 'Scheduling',
          context: 'Time availability',
          projectId,
          employeeIds,
          origin: 'Scheduling Board'
        });
      }
    },
    [addSchedules, refetchWarnings, wrapApiCall]
  );

  const moveSlotUseCase = useCallback(
    async ({ projectId, shiftId, slotId, dst, markAsOpenShift, decreaseNeeded, bookToLinkedShifts = false }) => {
      const {
        labelId: baseLabelId,
        locationId: baseLocationId,
        date,
        positionId,
        employeeId,
        startTime,
        endTime
      } = dst;

      const formattedDate = moment(date).format('YYYY-MM-DD');
      const labelId = baseLabelId ? parseInt(baseLabelId, 10) : undefined;
      const locationId = baseLocationId || (labelId ? mappedLabels[labelId]?.locationId : locations[0]?.id);

      const movePayload = {
        projectId,
        shiftId,
        slotId,
        markAsOpenShift,
        decreaseNeeded,
        bookToLinkedShifts,
        dst: {
          labelId,
          locationId,
          date: formattedDate,
          positionId,
          employeeId,
          startTime,
          endTime
        }
      };

      if (wrapApiCall) {
        await wrapApiCall(() => moveSlot({ ...movePayload, bypassComplianceWarnings: false }), {
          actionFn: () => {
            return moveSlot({ ...movePayload, bypassComplianceWarnings: true });
          },
          onSuccess: () => refetchWarnings?.()
        });
      } else {
        await moveSlot(movePayload);
        refetchWarnings?.();
      }
    },
    [locations, mappedLabels, moveSlot, refetchWarnings, wrapApiCall]
  );

  const createNewOpenShift = useCallback(
    async ({
      labelId,
      locationId,
      date,
      startTime,
      endTime,
      needed = 1,
      positionId,
      areaId,
      refetchProjects,
      isOpenShift = true,
      shiftTags
    }) => {
      const data = await getOrCreateBulkProject({
        projects: [
          { labelId, locationId, needed, date, startTime, endTime, positionId, isOpenShift, areaId, shiftTags }
        ]
      });

      // get the most updated shift id - the max
      // add here if needed comparison with other variavles to make
      // sure it's the relevent shift - positionId, start & end, etc
      const shiftId = Math.max(...(data?.[0]?.shifts || []).map(({ id }) => id));

      const positionFieldID = filters?.employees?.basic?.find(({ slug }) => slug === 'positions')?.id;

      await updatePublications({
        kind: 'apply',
        branches: [selectedBranchId],
        items: [
          {
            id: shiftId,
            filters: { basic: [{ id: positionFieldID, value: [positionId] }] },
            audience: { all: true }
          }
        ],
        checkedValue: { all: true }
      });
      refetchProjects && refetch();
      refetchWarnings?.();
    },
    [filters?.employees?.basic, getOrCreateBulkProject, refetch, refetchWarnings, selectedBranchId, updatePublications]
  );

  const createOpenShiftWithBookedEmployees = useCallback(
    async ({
      labelId,
      startTime,
      endTime,
      needed = 1,
      newBookedEmployees,
      positionId,
      publishShift = false,
      locationId,
      areaId,
      branchCostCenterId,
      clientAdminId,
      clientBranchId,
      refetchProjects,
      isOpenShift = true,
      bookDays = [],
      shiftTags
    }) => {
      const shiftGroupId = uuidv4();

      // Always create ONE project with week boundaries and all shifts
      const sourceDate = bookDays[0];
      const { weekStartDate, weekEndDate } = getWeekBoundaries(sourceDate, firstDayOfWeek);

      const shifts = bookDays.map((bookDate) => {
        const { startTime: startTimeRange, endTime: endTimeRange } = getTimeRangeWithDates({
          startTime,
          endTime,
          date: bookDate
        });

        return {
          labelId: labelId ? parseInt(labelId, 10) : undefined,
          branchCostCenterId,
          needed,
          positionId,
          startTime: startTimeRange,
          endTime: endTimeRange,
          isOpenShift,
          shiftGroupId: bookDays.length > 1 ? shiftGroupId : undefined,
          shiftTags,
          ...(areaId && { locationId: areaId })
        };
      });

      const formattedLocationId =
        locationId || (labelId ? mappedLabels[parseInt(labelId, 10)]?.locationId : locations[0]?.id);

      const { data } =
        (await addProjects({
          projects: [
            {
              labelId: labelId ? parseInt(labelId, 10) : undefined,
              locationId: formattedLocationId,
              date: weekStartDate,
              endDate: weekEndDate,
              clientBranchId,
              clientAdminId,
              isWeeklyPlanner: true,
              shifts
            }
          ],
          isSchedulingBoard: true
        })) || {};

      if (newBookedEmployees?.length > 0) {
        // Only book employees to the newly created shifts, not all shifts in the project
        // We created bookDays.length shifts, so we pick the N shifts with the highest IDs (newest)
        const numberOfShiftsCreated = bookDays.length;

        try {
          await Promise.all(
            (data || []).map((newProjectData) => {
              const allShifts = newProjectData?.shifts || [];

              // Sort shifts by ID descending and take the N newest ones
              const newlyCreatedShifts = [...allShifts].sort((a, b) => b.id - a.id).slice(0, numberOfShiftsCreated);

              if (newlyCreatedShifts.length === 0) {
                return Promise.resolve();
              }

              return bookBulkEmployeesToShift({
                projectId: newProjectData.id,
                shifts: newlyCreatedShifts.map((shift) => ({
                  shiftId: shift.id,
                  employeeIds: newBookedEmployees.map(({ id }) => id)
                }))
              });
            })
          );
        } catch (error) {
          // If error was handled by wrapApiCall (compliance violation), re-throw it
          // so the outer wrapApiCall in bookingActionsFactory can handle it
          // The modal is already showing from bookBulkEmployeesToShift
          if (error._handled) {
            return;
          }
          // Re-throw other errors normally
          throw error;
        }
      }

      if (publishShift) {
        const positionFieldID = filters?.employees?.basic?.find(({ slug }) => slug === 'positions')?.id;
        await Promise.all(
          (data || []).map((newProjectData) =>
            updatePublications({
              kind: 'apply',
              branches: [selectedBranchId],
              items: (newProjectData?.shifts || []).map((shift) => ({
                id: shift.id,
                filters: { basic: [{ id: positionFieldID, value: [positionId] }] },
                audience: { all: true }
              })),
              checkedValue: { all: true }
            })
          )
        );
      }
      refetchProjects && refetch();
      refetchWarnings?.();
    },
    [
      addProjects,
      bookBulkEmployeesToShift,
      filters?.employees?.basic,
      firstDayOfWeek,
      locations,
      mappedLabels,
      refetch,
      refetchWarnings,
      selectedBranchId,
      updatePublications
    ]
  );

  const addShiftWithEmployeeBooked = useCallback(
    async ({ data, positionId, employeeId, startTime, endTime, refetchProjects }) => {
      //   adding a shift + booking the employee
      await addShiftWithEmployee({
        accountId,
        branchId: selectedBranchId,
        projectId: data?.[0]?.id,
        values: {
          positionId,
          needed: 1,
          employeeId,
          startTime,
          endTime
        }
      });

      refetchWarnings?.();
      refetchProjects && refetch();
    },
    [accountId, addShiftWithEmployee, refetch, refetchWarnings, selectedBranchId]
  );

  const updateShiftParams = useCallback(
    async ({ projectId, shift, refetchProjects, notifyEmployeesUponChange }) => {
      await editProject({
        projectId,
        shifts: { edited: [shift] },
        notifyEmployeesUponChange
      });

      refetchProjects && refetch();
      refetchWarnings?.();
    },
    [editProject, refetch, refetchWarnings]
  );

  const editBookedShift = useCallback(
    async ({
      projectId,
      shiftId,
      slotId,
      notes,
      startTime,
      endTime,
      breaktimes,
      refetchProjects,
      branchCostCenterId,
      positionId,
      applyToLinkedShifts,
      shiftTags,
      notifyEmployeesUponChange
    }) => {
      await updateSlot({
        projectId,
        shiftId,
        slotId,
        src: 'time-availability- editBookedShift',
        slot: {
          notes: notes || undefined,
          startTime,
          endTime,
          breaktimes
        }
      });

      const updateShiftValues = omitBy(
        { branchCostCenterId, startTime, endTime, positionId, applyToLinkedShifts, shiftTags },
        isUndefined
      );

      if (Object.keys(updateShiftValues).length > 0) {
        await updateShiftParams({
          projectId,
          shift: { id: shiftId, ...updateShiftValues },
          refetchProjects: false,
          notifyEmployeesUponChange
        });
      }

      refetchProjects && refetch();
      refetchWarnings?.();
    },
    [refetch, refetchWarnings, updateShiftParams, updateSlot]
  );

  const updateTimeSheet = useCallback(
    ({ timesheetId, startTime, endTime }) =>
      api.updateTimeSheet({ accountId, timesheetId, start: { time: startTime }, end: { time: endTime } }),
    [accountId]
  );

  const editBookedShiftTimesheet = useCallback(
    async ({ clockIn, clockOut, timesheetId, breaktimes, refetchProjects }) => {
      await updateTimeSheet({ timesheetId, startTime: clockIn, endTime: clockOut, breaktimes });

      refetchProjects && refetch();
      refetchWarnings?.();
    },
    [refetch, refetchWarnings, updateTimeSheet]
  );

  const deleteEmployeeBookingFromShift = useCallback(
    async ({
      projectId,
      shiftId,
      slotId,
      markAsOpenShift,
      decreaseNeeded,
      unbookFromLinkedShifts,
      bypassComplianceWarnings,
      refetchProjects
    }) => {
      await removeSchedule({
        projectId,
        shiftId,
        slotId,
        markAsOpenShift,
        decreaseNeeded,
        unbookFromLinkedShifts,
        bypassComplianceWarnings
      });

      refetchProjects && refetch();
      refetchWarnings?.();
    },
    [refetch, refetchWarnings, removeSchedule]
  );

  const deleteOpenShift = useCallback(
    async ({ shiftId, projectId, applyShiftDeletionToLinkedShifts, refetchProjects }) => {
      await removeShift({
        shiftId,
        projectId,
        applyShiftDeletionToLinkedShifts
      });
      refetchProjects && refetch();
      refetchWarnings?.();
    },
    [refetch, refetchWarnings, removeShift]
  );

  const editOpenShift = useCallback(
    async ({
      projectId,
      shift,
      shiftDetailsChanged = false,
      unbookedEmployees,
      newBookedEmployees,
      labelId,
      locationId,
      refetchProjects,
      markAsOpenShift,
      decreaseNeeded,
      applyShiftUpdatesToLinked = false,
      bookEmployeesToLinked = false
    }) => {
      if (shiftDetailsChanged) {
        // Build the shift object with per-shift linked flag if applicable
        const shiftWithLinkedFlag = applyShiftUpdatesToLinked ? { ...shift, applyToLinkedShifts: true } : shift;

        await editProject({
          projectId,
          labelId,
          locationId,
          shifts: { edited: [shiftWithLinkedFlag] }
        });
      }

      if (unbookedEmployees?.length > 0) {
        await Promise.all(
          unbookedEmployees
            .filter(({ slotId }) => !!slotId)
            .map(({ slotId }) =>
              removeSchedule({ projectId, shiftId: shift.id, slotId, markAsOpenShift, decreaseNeeded })
            )
        );
      }

      if (newBookedEmployees?.length > 0) {
        await bookBulkEmployeesToShift({
          projectId,
          shifts: [
            {
              shiftId: shift.id,
              employeeIds: newBookedEmployees.map(({ id }) => id),
              bookToLinkedShifts: bookEmployeesToLinked
            }
          ]
        });
      }

      refetchProjects && refetch();
      refetchWarnings?.();
    },
    [bookBulkEmployeesToShift, editProject, refetch, refetchWarnings, removeSchedule]
  );

  const deleteShiftFromEmployeeBooking = useCallback(
    async ({ projectId, shift, slotId, refetchProjects }) => {
      await Promise.all([
        deleteEmployeeBookingFromShift({ projectId, shiftId: shift.id, slotId }),
        updateShiftParams({ projectId, shift, refetchProjects })
      ]);

      refetchProjects && refetch();
      refetchWarnings?.();
    },
    [deleteEmployeeBookingFromShift, refetch, refetchWarnings, updateShiftParams]
  );

  const handleUpdateTimesheet = useCallback(
    async ({ projectId, date, timesheetId, clockIn, clockOut, shiftId, slotId, notes, breaktimes }) => {
      if (!notes && !clockIn && !clockOut && !breaktimes) {
        return;
      }

      if (notes || breaktimes) {
        await editBookedShift({
          projectId,
          notes,
          shiftId,
          slotId,
          breaktimes,
          refetchProjects: false
        });
      }
      if (clockIn || clockOut) {
        await editBookedShiftTimesheet({
          date,
          timesheetId,
          clockIn,
          clockOut,
          refetchProjects: false
        });
      }
    },

    [editBookedShift, editBookedShiftTimesheet]
  );

  const bookingActionsFactory = useCallback(
    ({ type, values, refetchProjects = false }) => {
      const data = { ...values, refetchProjects };
      switch (type) {
        case BOOKING_ACTIONS.bookToExistingShift: {
          return onBookToExistingShift(data);
        }

        case BOOKING_ACTIONS.bookToNewShift: {
          return getOrCreateProjectAndBook(data);
        }

        case BOOKING_ACTIONS.addShiftWithEmployeeBooked: {
          return addShiftWithEmployeeBooked(data);
        }

        case BOOKING_ACTIONS.createOpenShift: {
          return createNewOpenShift(data);
        }

        case BOOKING_ACTIONS.createOpenShiftWithBookedEmployees: {
          return createOpenShiftWithBookedEmployees(data);
        }

        case BOOKING_ACTIONS.editBookedShift: {
          return editBookedShift(data);
        }

        case BOOKING_ACTIONS.editBookedShiftTimesheet: {
          return editBookedShiftTimesheet(data);
        }

        case BOOKING_ACTIONS.editOpenShift: {
          return editOpenShift(data);
        }

        case BOOKING_ACTIONS.deleteBookedShift: {
          return deleteEmployeeBookingFromShift(data);
        }

        case BOOKING_ACTIONS.deleteOpenShift: {
          return deleteOpenShift(data);
        }

        case BOOKING_ACTIONS.moveSlot: {
          return moveSlotUseCase(data);
        }

        case BOOKING_ACTIONS.updateShiftParams: {
          return updateShiftParams(data);
        }

        case BOOKING_ACTIONS.deleteShiftFromEmployeeBooking: {
          return deleteShiftFromEmployeeBooking(data);
        }

        case BOOKING_ACTIONS.handleUpdateTimesheet: {
          return handleUpdateTimesheet(data);
        }

        default:
          return null;
      }
    },
    [
      onBookToExistingShift,
      getOrCreateProjectAndBook,
      addShiftWithEmployeeBooked,
      createNewOpenShift,
      createOpenShiftWithBookedEmployees,
      editBookedShift,
      editBookedShiftTimesheet,
      editOpenShift,
      deleteEmployeeBookingFromShift,
      deleteOpenShift,
      moveSlotUseCase,
      updateShiftParams,
      deleteShiftFromEmployeeBooking,
      handleUpdateTimesheet
    ]
  );

  return { bookingActionsFactory };
};

export default useTimeAvailabilityBookingOptions;
